import { CssBaseline, GlobalStyles, ThemeProvider, createTheme } from '@mui/material';
import { FC, PropsWithChildren } from 'react';
import { ColorsProvider } from './colors.provider';
import { COLORS, DEFAULT_BORDER_RADIOS } from './main-app.theme';
import { Colors } from './types';

const FORMS_COLORS: Colors = {
  ...COLORS,
  others: {
    greenLight: '#BACFBE',
    greenHard: '#195F27',
    red: '#D93025',
    black: '#202124',
    blackLight: '#4C4B4B',
    white: '#FFFFFF',
    grayLight: '#F6F6F6',
    gray: '#E8E8E8',
    grayMedium: '#ACACAC',
    grayHard: '#7F7F7F',
  },
};

const FORMS_THEME = createTheme({
  palette: {
    background: {
      default: COLORS.background.main,
    },
    primary: {
      main: COLORS.primary.main,
    },
    secondary: {
      main: COLORS.secondary.main,
    },
    divider: COLORS.others.grayLight,
  },
  typography: {
    fontFamily: 'CommuterSans, sans-serif',
    h1: {
      fontSize: '1.5rem',
      color: COLORS.others.black,
      marginBottom: 10,
      fontWeight: 'bold',
    },
    h2: {
      fontSize: '0.9rem',
      color: COLORS.others.black,
    },
    h3: {
      fontSize: '0.9rem',
      color: COLORS.others.black,
    },
    h4: {
      fontSize: '0.8rem',
      fontWeight: 'bold',
      color: COLORS.others.black,
    },
    h5: {
      fontSize: '0.7rem',
      color: COLORS.others.black,
    },
    h6: {
      fontSize: '0.6rem',
      color: COLORS.others.black,
    },
    body1: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '14px',
      color: COLORS.others.blackLight,
    },
    body2: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '14px',
      color: COLORS.others.blackLight,
    },
  },
  components: {
    MuiAlert: {
      styleOverrides: {
        root: {
          'backgroundColor': COLORS.ctas.secondary,
          'color': COLORS.primary.contrast,
          'fontFamily': 'Roboto, sans-serif',
          'fontSize': '14px',
          '& .MuiAlert-standardSuccess > .MuiAlert-icon': {
            width: '18px',
            height: '18px',
          },
        },
        standardSuccess: {
          '.MuiAlert-icon': {
            color: COLORS.success.dark,
          },
        },
        standardError: {
          '.MuiAlert-icon': {
            color: COLORS.error.main,
          },
        },
        standardWarning: {
          '.MuiAlert-icon': {
            color: COLORS.warning.main,
          },
        },
        standardInfo: {
          '.MuiAlert-icon': {
            color: COLORS.primary.main,
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingRight: '5px',
          paddingLeft: '5px',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          '& .required': {
            fontWeight: 'bold',
            color: COLORS.others.red,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          '&.header': {
            padding: '16px',
          },

          // Breakpoint (xs)
          '@media (max-width:600px)': {
            padding: '5px',
          },

          // Breakpoint (md)
          '@media (min-width:601px)': {
            padding: '32px 24px 16px 24px',
            marginBottom: '16px',
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          '&.matrix': {
            // Breakpoint (xs)
            '@media (max-width:600px)': {
              padding: '9px 4px 9px 4px',
            },

            // Breakpoint (md)
            '@media (min-width:601px)': {
              padding: '9px',
            },
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0,
        },
      },
    },
  },
});

const FormsTheme: FC<PropsWithChildren> = ({ children }) => (
  <ThemeProvider theme={FORMS_THEME}>
    <GlobalStyles
      styles={{
        body: {
          '& .firebase-emulator-warning': {
            display: 'none',
          },
          // it's not worth to modify firefox scrollbar
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
            background: 'inherit',
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            backgroundColor: COLORS.primary.main,
            borderRadius: '10px',
          },
        },
      }}
    />
    <CssBaseline />
    <ColorsProvider colors={FORMS_COLORS} borderRadiusDefault={DEFAULT_BORDER_RADIOS}>
      {children}
    </ColorsProvider>
  </ThemeProvider>
);

export { FORMS_COLORS, FormsTheme };
